// The `browser` subpath, not the bare package: bwip-js's root export keys its
// conditions by environment (browser / node / react-native), none of which
// TypeScript's `bundler` resolution selects, so `import 'bwip-js'` resolves at
// runtime and fails typecheck. This subpath is a flat types/import/require
// entry. It is pure JS with no canvas dependency for `toSVG`/`raw`, so it runs
// unchanged in the node environment vitest uses.
import bwipjs from 'bwip-js/browser'

/**
 * The scannable symbol on an inventory label, as inline SVG.
 *
 * QR, AND THE REASONING MATTERS, because it reversed twice.
 *
 * On paper the regulated answer is Data Matrix: ICCBBA specifies it for ISBT 128
 * two-dimensional symbols on cell-therapy products, and GS1 DataMatrix is what
 * FDA UDI and EU FMD put on small regulated items. That answer is correct for a
 * dedicated 2D imaging scanner on a bench.
 *
 * It is the wrong answer for the hardware this workflow actually uses. Operators
 * scan with a PHONE. iOS Camera and Google Lens detect QR natively and
 * out-of-the-box; Data Matrix support across phone cameras is patchy to absent,
 * so a technically-superior symbol that the device will not look for is worth
 * nothing on a shelf. These are also internal identifiers — a lot in a bin, a
 * bin on a rack — not the regulated product identity on a patient-bound bag.
 * When the product/DIN label is built, THAT one should carry ISBT 128 Data
 * Matrix, and it is a different label with a different reader.
 *
 * QUIET ZONE IS FOUR MODULES, NOT ONE. This is a hard requirement of the QR
 * spec and a common cause of "renders fine, will not scan" — Data Matrix needs
 * only one, and carrying that assumption over is a real bug, not a nicety.
 *
 * The symbol is bwip-js's own drawing, taken verbatim. An earlier hand-rolled
 * encoder shipped symbols that rendered perfectly and scanned as nothing: its
 * test pinned the codeword stream, so the data was provably right while the
 * module placement was silently wrong. There is no visual tell with these
 * symbologies, so the geometry does not get retyped by us.
 */

export interface LabelSymbolOptions {
  /**
   * Pixel size of one module. The default targets a symbol a phone can lock
   * onto from normal working distance rather than one the operator has to
   * hunt for.
   */
  moduleSize?: number
  /** Module colour. Light modules stay transparent so the paper shows. */
  color?: string
  /** Accessible name; defaults to the encoded value. */
  title?: string
}

/** Quiet zone in modules. Four is the QR specification's floor, not a margin. */
const QUIET_MODULES = 4

/**
 * Error correction level.
 *
 * M (~15% recovery) rather than L: these labels live in freezers and on gloved
 * hands, so frost, scuffs and a thumb over one corner are the normal case. Going
 * higher than M would buy more recovery at the cost of more modules, and more
 * modules at a fixed physical size means each one is smaller — which is the
 * thing that actually stops a phone resolving the symbol.
 */
const EC_LEVEL: EcLevel = 'M'

type EcLevel = 'L' | 'M' | 'Q' | 'H'

/**
 * `eclevel` is a QR-specific BWIPP option. bwip-js honours it at runtime but its
 * TypeScript types only describe the options common to every symbology, so it is
 * declared here rather than cast away — the value stays checked, and the gap is
 * recorded instead of hidden behind an `as any`.
 */
type QrRenderOptions = Parameters<typeof bwipjs.toSVG>[0] & { eclevel?: EcLevel }
type QrRawOptions = Parameters<typeof bwipjs.raw>[0] & { eclevel?: EcLevel }

/** Longest payload a label symbol may carry. */
export const MAX_PAYLOAD_CHARS = 96

/** Whether `value` can be carried by a label symbol. */
export function isEncodable(value: string): boolean {
  if (value.length === 0 || value.length > MAX_PAYLOAD_CHARS) return false
  return /^[\x20-\x7e]+$/.test(value)
}

/**
 * The symbol's modules, row-major, true = dark.
 *
 * Exposed so a test can assert the finder patterns are where they belong —
 * the check that catches a transposed or mirrored grid.
 */
export function labelSymbolModules(value: string): boolean[][] {
  if (!isEncodable(value)) throw new Error(`Cannot encode ${JSON.stringify(value)} as QR`)
  const rawOpts: QrRawOptions = { bcid: 'qrcode', text: value, eclevel: EC_LEVEL }
  const [symbol] = bwipjs.raw(rawOpts)
  // `raw` is typed as a union: linear symbologies hand back bar/space widths,
  // matrix ones a pixel grid. Narrow rather than cast — the linear shape here
  // would mean we asked for the wrong symbology, and reading undefined widths
  // would quietly draw an empty symbol.
  if (!symbol || !('pixs' in symbol)) return []
  const cols = symbol.pixx
  const rows = symbol.pixy
  const out: boolean[][] = []
  for (let r = 0; r < rows; r++) {
    const line: boolean[] = []
    for (let c = 0; c < cols; c++) line.push(symbol.pixs[r * cols + c] === 1)
    out.push(line)
  }
  return out
}

/**
 * Render `value` as an inline SVG QR code.
 *
 * Returns an empty string for content that cannot be encoded: callers print the
 * human-readable text instead, which is far better than printing a symbol that
 * scans as something else.
 */
export function labelSymbolSvg(value: string, opts: LabelSymbolOptions = {}): string {
  const moduleSize = opts.moduleSize ?? 6
  const color = opts.color ?? '#000000'

  if (!isEncodable(value)) return ''

  let inner: string
  let units: number
  try {
    const renderOpts: QrRenderOptions = {
      bcid: 'qrcode',
      text: value,
      eclevel: EC_LEVEL,
      scale: 1,
      includetext: false,
    }
    const svg = bwipjs.toSVG(renderOpts)
    const box = /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/.exec(svg)
    const path = /<path\b[^>]*\/?>/.exec(svg)
    if (!box || !path) return ''
    units = Number(box[1])
    inner = path[0].replace(/\s*\/?>$/, ` fill="${color}"/>`)
  } catch {
    return ''
  }

  // bwip-js draws two user units per module at scale 1.
  const unitsPerModule = 2
  const modules = units / unitsPerModule
  const quietUnits = QUIET_MODULES * unitsPerModule
  const px = (modules + QUIET_MODULES * 2) * moduleSize
  const title = escapeXml(opts.title ?? value)

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(px)}" height="${round(px)}" ` +
    `viewBox="${-quietUnits} ${-quietUnits} ${units + quietUnits * 2} ${units + quietUnits * 2}" ` +
    // A white ground behind the quiet zone: on screen the dialog sits on a tinted
    // surface, and a QR whose quiet zone is not actually white will not scan.
    `shape-rendering="crispEdges" role="img" aria-label="${title}">` +
    `<title>${title}</title>` +
    `<rect x="${-quietUnits}" y="${-quietUnits}" width="${units + quietUnits * 2}" ` +
    `height="${units + quietUnits * 2}" fill="#ffffff"/>${inner}</svg>`
  )
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000
}

function escapeXml(s: string): string {
  return s.replace(
    /[<>&"']/g,
    (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c] ?? c,
  )
}
